import {
  hasKioskNativeShell,
  KioskLaunch,
  toSupportedKioskLanguage,
} from "./kiosk-launch";

const ordinaryWebLaunch: KioskLaunch = {
  isKiosk: false,
  hasNativeShell: false,
  language: null,
};

const pendingBootstrapRequests = new WeakMap<Window, Promise<KioskLaunch>>();

const isKioskBootstrapPayload = (
  payload: unknown,
): payload is { version: 1; language: string } =>
  typeof payload === "object" &&
  payload !== null &&
  "version" in payload &&
  payload.version === 1 &&
  "language" in payload &&
  typeof payload.language === "string";

export const requestKioskBootstrap = (
  target: Window = window,
  timeoutMs = 2000,
): Promise<KioskLaunch> => {
  const handler = target.kioskBridgeHandler;
  if (!hasKioskNativeShell(target) || !handler) {
    return Promise.resolve(ordinaryWebLaunch);
  }

  const pendingRequest = pendingBootstrapRequests.get(target);
  if (pendingRequest) return pendingRequest;

  const request = new Promise<KioskLaunch>((resolve, reject) => {
    const bridge: NonNullable<Window["fitnextKioskBootstrapV1"]> = {
      initialize(payload) {
        if (!isKioskBootstrapPayload(payload)) {
          finish(() => reject(new Error("Invalid kiosk bootstrap payload")));
          return;
        }

        const language = toSupportedKioskLanguage(payload.language);
        if (!language) {
          finish(() => reject(new Error("Invalid kiosk bootstrap payload")));
          return;
        }

        finish(() =>
          resolve({ isKiosk: true, hasNativeShell: true, language }),
        );
      },
    };

    const timeout = window.setTimeout(
      () => finish(() => reject(new Error("Kiosk bootstrap timed out"))),
      timeoutMs,
    );

    const finish = (settle: () => void) => {
      window.clearTimeout(timeout);
      if (target.fitnextKioskBootstrapV1 === bridge) {
        delete target.fitnextKioskBootstrapV1;
      }
      settle();
    };

    target.fitnextKioskBootstrapV1 = bridge;

    try {
      handler.postMessage(
        JSON.stringify({ version: 1, status: "bootstrap-request" }),
      );
    } catch (error) {
      finish(() => reject(error));
    }
  });

  pendingBootstrapRequests.set(target, request);
  void request.then(
    () => {
      if (pendingBootstrapRequests.get(target) === request) {
        pendingBootstrapRequests.delete(target);
      }
    },
    () => {
      if (pendingBootstrapRequests.get(target) === request) {
        pendingBootstrapRequests.delete(target);
      }
    },
  );

  return request;
};
